import type { ReactNode } from "react";
import { useState } from "react";
import { useT } from "../../i18n/index.js";

/**
 * F1 do redesenho (CEO 2026-09-23) — vocabulário de FORMULÁRIO.
 *
 * "quero deixar o design do Gestion parecido ao eDSP sem parecer uma cópia":
 * o que se adota é a GRAMÁTICA que a mão do operador já conhece (rótulo com
 * etiqueta de obrigatório, caixa de dica, par Não/Sim, acordeão, barra de
 * ações no rodapé, zona de perigo no fim). A identidade — navy+lima, Saira
 * Condensed, tema claro, ladrilho no título, trilho lima na dica — é nossa.
 *
 * Os estilos vivem em `styles/erp-shell.css` (classes `.f*`, escopadas em
 * `.erp` e em tokens theme-aware). Estes componentes são só a montagem: assim
 * o tema claro/escuro continua sendo assunto de UM lugar só.
 */

export function FormPage({
  icon,
  title,
  subtitle,
  children,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="fpage">
      <header className="fpage__head">
        {icon && <span className="fpage__ico">{icon}</span>}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </header>
      {children}
    </div>
  );
}

export function FormSection({
  title,
  desc,
  actions,
  children,
}: {
  title?: string;
  desc?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="fsec">
      {(title || desc || actions) && (
        <div className="flex items-start justify-between gap-3">
          <div className="fsec__head">
            {title && <span className="fsec__title">{title}</span>}
            {desc && <span className="fsec__desc">{desc}</span>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Grade responsiva de campos (1 → N colunas conforme a largura). */
export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="fgrid">{children}</div>;
}

/**
 * Campo. `required` mostra a etiqueta "Campo obrigatório" ao lado do rótulo —
 * é o sinal que o operador do eDSP procura antes de tentar gravar.
 */
export function Field({
  label,
  required,
  help,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  error?: string | null;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <label className="ffield">
      <span className="flab">
        {label}
        {required && <span className="freq">{t("common.requiredField")}</span>}
      </span>
      {children}
      {help && <span className="fhelp">{help}</span>}
      {error && <span className="ferr">{error}</span>}
    </label>
  );
}

/** Caixa de dica (o 💡 do eDSP, com o trilho lima nosso). */
export function Hint({ children }: { children: ReactNode }) {
  return (
    <p className="fhint">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" />
      </svg>
      <span>{children}</span>
    </p>
  );
}

/**
 * Par de escolha — dois alvos grandes com a bolinha à direita. Substitui o
 * checkbox solto onde a resposta é binária e importa (Não/Sim, Privado/Grupo).
 */
export function ChoicePair<T extends string | number | boolean>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T | null;
  options: [{ value: T; label: string }, { value: T; label: string }];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="fchoice">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={`fchoice__btn${value === o.value ? " on" : ""}`}
        >
          <span>{o.label}</span>
          <span className="fchoice__dot" />
        </button>
      ))}
    </div>
  );
}

/** Seção sanfonada das configurações (fechada por padrão). */
export function Accordion({
  title,
  desc,
  defaultOpen = false,
  children,
}: {
  title: string;
  desc?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`facc${open ? " open" : ""}`}>
      <button type="button" className="facc__head" onClick={() => setOpen((v) => !v)}>
        <span className="facc__t">
          <b>{title}</b>
          {desc && <span>{desc}</span>}
        </span>
        <span className="facc__chev">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && <div className="facc__body">{children}</div>}
    </section>
  );
}

/** Zona de perigo — ação irreversível, sempre no fim da página. */
export function DangerZone({
  title,
  desc,
  buttonLabel,
  onClick,
  disabled,
}: {
  title: string;
  desc?: string;
  buttonLabel: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <section className="fdanger">
      <span className="fdanger__t">{title}</span>
      {desc && <span className="fdanger__d">{desc}</span>}
      <button type="button" className="fdanger__btn" onClick={onClick} disabled={disabled}>
        {buttonLabel}
      </button>
    </section>
  );
}

/**
 * Barra de ações do rodapé. O `message` aparece AQUI, ao lado do botão —
 * mensagem de erro no topo da página some da tela no celular (lição da
 * recepção do técnico).
 */
export function PageActions({
  backLabel,
  onBack,
  saveLabel,
  onSave,
  saving,
  disabled,
  message,
}: {
  backLabel?: string;
  onBack?: () => void;
  saveLabel: string;
  onSave: () => void;
  saving?: boolean;
  disabled?: boolean;
  message?: { tone: "ok" | "err"; text: string } | null;
}) {
  return (
    <div className="factions">
      <div className="flex items-center gap-3">
        {onBack && backLabel && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
            {backLabel}
          </button>
        )}
        {message && <span className={`factions__msg ${message.tone}`}>{message.text}</span>}
      </div>
      <button
        type="button"
        className="btn btn--primary"
        onClick={onSave}
        disabled={saving || disabled}
      >
        {saveLabel}
      </button>
    </div>
  );
}
